import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Box, Alert, CircularProgress, Typography, InputAdornment,
  FormControlLabel, Checkbox, Divider,
} from '@mui/material';
import { CreditCard as CardIcon, Lock as LockIcon } from '@mui/icons-material';
import { useAcceptJs } from 'react-acceptjs';
import axios from '../../../api/axios';

const PAYMENT_METHODS = [
  { value: 'Check', label: 'Check (Mail)' },
  { value: 'Credit Card', label: 'Credit Card (Phone)' },
  { value: 'Other', label: 'Other' },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY',
];

const ApplyPaymentModal = ({ open, order, onClose, token }) => {
  const authData = useMemo(() => ({
    apiLoginID: process.env.REACT_APP_apiLoginID || '',
    clientKey: process.env.REACT_APP_clientKey || '',
  }), []);

  const { dispatchData, loading: acceptLoading } = useAcceptJs({
    environment: 'PRODUCTION',
    authData,
  });

  const [form, setForm] = useState({
    paymentMethod: 'Check',
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    referenceNumber: '',
    notes: '',
    sendReceipt: false,
  });

  const [cardData, setCardData] = useState({
    cardNumber: '',
    nameOnCard: '',
    expMonth: '',
    expYear: '',
    cardCode: '',
  });

  const [billing, setBilling] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: 'NC',
    zip: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [cardErrors, setCardErrors] = useState({});

  // Profile with its settings nested on it: { ...userFields, userSettings: {...} }
  const [user, setUser] = useState(null);

  const isCC = form.paymentMethod === 'Credit Card';

  // Load the order owner's profile (and settings) when the modal opens.
  useEffect(() => {
    if (!open || !order?.UserID) {
      setUser(null);
      return;
    }

    let cancelled = false;
    setUser(null);

    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const loadUser = async () => {
      try {
        const res = await axios.get(`/api/users/${order.UserID}`, { headers, withCredentials: true });
        if (cancelled) return;

        const data = res?.data ?? {};
        // Accept { user, userSettings }, { user: { ..., userSettings } },
        // or the user object itself with userSettings nested.
        const profile = data.user ?? data;
        const settings = data.userSettings ?? profile?.userSettings ?? null;

        setUser({ ...profile, userSettings: settings });
      } catch (e) {
        if (!cancelled) console.error('Failed to load order user:', e);
      }
    };

    loadUser();

    return () => { cancelled = true; };
  }, [open, order?.UserID, token]);

  // Reset the form each time the modal opens for an order.
  useEffect(() => {
    if (open && order) {
      const balance = Number(order.Balance) || Number(order.AmountDue) || 0;
      setForm({
        paymentMethod: 'Check',
        amount: balance > 0 ? balance.toFixed(2) : '',
        paymentDate: new Date().toISOString().split('T')[0],
        referenceNumber: '',
        notes: '',
        sendReceipt: false,
      });
      setCardData({ cardNumber: '', nameOnCard: '', expMonth: '', expYear: '', cardCode: '' });

      // Start from what the order itself knows; the profile prefill below
      // layers the volunteer's saved details on top once they load.
      const nameParts = (order?.CustomerName || '').trim().split(/\s+/);
      const derivedFirst = nameParts.length > 0 ? nameParts[0] : '';
      const derivedLast = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      setBilling({
        firstName: derivedFirst,
        lastName: derivedLast,
        address: order.Address || '',
        city: order.City || '',
        state: order.State || 'NC',
        zip: order.Zip || '',
      });

      setError(null);
      setCardErrors({});
    }
  }, [open, order]);

  // Prefill billing (and name on card) when the volunteer's data arrives.
  // Separate from the reset above so a late response never wipes what the
  // admin has already typed into the rest of the form.
  useEffect(() => {
    if (!open || !user) return;

    const s = user.userSettings || {};

    setBilling((prev) => ({
      ...prev,
      firstName: user.FirstName || prev.firstName,
      lastName: user.LastName || prev.lastName,
      address: s.BP_G_Address || prev.address,
      city: s.BP_G_AddressCity || s.BP_G_City || prev.city,
      state: s.BP_G_AddressState || s.BP_G_State || prev.state || 'NC',
      zip: s.BP_G_AddressZip || s.BP_G_Zip || prev.zip,
    }));

    const full = `${user.FirstName || ''} ${user.LastName || ''}`.trim();
    if (full) {
      setCardData((prev) => (prev.nameOnCard ? prev : { ...prev, nameOnCard: full }));
    }
  }, [open, user]);

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleCardChange = (field) => (e) => {
    setCardData(prev => ({ ...prev, [field]: e.target.value }));
    if (cardErrors[field]) setCardErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleBillingChange = (field) => (e) => {
    setBilling(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleExpChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw.length <= 2) {
      setCardData(prev => ({ ...prev, expMonth: raw, expYear: '' }));
    } else {
      setCardData(prev => ({ ...prev, expMonth: raw.slice(0, 2), expYear: raw.slice(2, 4) }));
    }
    if (cardErrors.expiration) setCardErrors(prev => ({ ...prev, expiration: undefined }));
  };

  const expDisplay = cardData.expMonth && cardData.expYear
    ? `${cardData.expMonth}/${cardData.expYear}`
    : cardData.expMonth || '';

  const validateCard = () => {
    const errs = {};
    const num = cardData.cardNumber.replace(/\s/g, '');
    if (!num) errs.cardNumber = 'Card number is required';
    else if (!/^\d{13,19}$/.test(num)) errs.cardNumber = 'Invalid card number';
    if (!cardData.nameOnCard.trim()) errs.nameOnCard = 'Name on card is required';
    if (!cardData.expMonth || !cardData.expYear) errs.expiration = 'Expiration is required';
    else {
      const m = parseInt(cardData.expMonth);
      if (m < 1 || m > 12) errs.expiration = 'Invalid month';
    }
    if (!cardData.cardCode.trim()) errs.cardCode = 'Security code is required';
    else if (!/^\d{3,4}$/.test(cardData.cardCode)) errs.cardCode = 'Invalid security code';
    if (!billing.firstName.trim()) errs.firstName = 'First name is required';
    if (!billing.lastName.trim()) errs.lastName = 'Last name is required';
    if (!billing.address.trim()) errs.address = 'Address is required';
    if (!billing.city.trim()) errs.city = 'City is required';
    if (!billing.zip.trim()) errs.zip = 'ZIP code is required';
    else if (!/^\d{5}(-\d{4})?$/.test(billing.zip.trim())) errs.zip = 'Invalid ZIP code';
    setCardErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      setError('Payment amount must be greater than zero');
      return;
    }
    if (!form.paymentMethod) {
      setError('Please select a payment method');
      return;
    }

    if (isCC) {
      if (!validateCard()) {
        setError('Please fix the card form errors below');
        return;
      }
      if (!authData.apiLoginID || !authData.clientKey) {
        setError('Payment gateway is not configured. Contact your administrator.');
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

      if (isCC) {
        const response = await dispatchData({
          cardData: {
            cardNumber: cardData.cardNumber.replace(/\s/g, ''),
            month: cardData.expMonth.padStart(2, '0'),
            year: cardData.expYear.padStart(2, '0'),
            cardCode: cardData.cardCode,
          },
        });

        if (response.messages.resultCode === 'Error') {
          const msg = response.messages.message?.[0]?.text || 'Card validation failed';
          setError(msg);
          setIsSubmitting(false);
          return;
        }

        const opaqueData = response?.opaqueData;

        await axios.post(
          `/api/orders/${order.OrderID}/process-card-payment`,
          {
            amount: Number(form.amount),
            opaqueData,
            billing: {
              firstName: billing.firstName,
              lastName: billing.lastName,
              address: billing.address,
              city: billing.city,
              state: billing.state,
              zip: billing.zip,
            },
            notes: form.notes,
            sendReceipt: form.sendReceipt,
          },
          { headers, withCredentials: true }
        );
      } else {
        await axios.post(
          `/api/orders/${order.OrderID}/payments`,
          {
            paymentMethod: form.paymentMethod,
            amount: Number(form.amount),
            paymentDate: form.paymentDate,
            referenceNumber: form.referenceNumber,
            notes: form.notes,
            sendReceipt: form.sendReceipt,
          },
          { headers, withCredentials: true }
        );
      }

      onClose(true);
    } catch (err) {
      let msg = 'Payment processing failed. Please try again.';
      if (err?.messages?.message?.[0]?.text) {
        msg = err.messages.message[0].text;
      } else if (err?.response?.data?.error) {
        msg = err.response.data.error;
      }
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!order) return null;

  const balance = Number(order.Balance) || Number(order.AmountDue) || 0;

  const fieldError = (field) => cardErrors[field] ? { error: true, helperText: cardErrors[field] } : {};

  const fieldSx = {
    '& .MuiInputBase-root': { height: 40, fontSize: '0.875rem', color: 'rgba(0,0,0,0.87)' },
    '& .MuiInputLabel-root': { fontSize: '0.875rem', color: 'rgba(0,0,0,0.6)' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.23)' },
    '& input::placeholder': { fontSize: '0.875rem', color: 'rgba(0,0,0,0.38)', opacity: 1 },
  };

  const menuItemSx = { fontSize: '0.875rem' };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#003063' }}>
        Apply Payment — Order #{order.OrderID}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Typography variant="body2">
            <strong>Customer:</strong> {order?.CustomerName || 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Current Balance:</strong>{' '}
            <span style={{ color: balance > 0 ? '#d32f2f' : '#2e7d32', fontWeight: 700 }}>
              ${balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            select
            label="Payment Method"
            value={form.paymentMethod}
            onChange={handleChange('paymentMethod')}
            fullWidth
            size="small"
            required
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          >
            {PAYMENT_METHODS.map(m => (
              <MenuItem key={m.value} value={m.value} sx={menuItemSx}>{m.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Payment Amount"
            placeholder="0.00"
            type="number"
            value={form.amount}
            onChange={handleChange('amount')}
            fullWidth
            size="small"
            required
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: <InputAdornment position="start" sx={{ '& p': { fontSize: '0.875rem' } }}>$</InputAdornment>,
            }}
            inputProps={{ min: 0.01, step: 0.01 }}
            sx={fieldSx}
          />

          {!isCC && (
            <>
              <TextField
                label="Payment Date"
                type="date"
                value={form.paymentDate}
                onChange={handleChange('paymentDate')}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />
              <TextField
                label="Reference Number"
                placeholder="Check # or Transaction ID"
                value={form.referenceNumber}
                onChange={handleChange('referenceNumber')}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />
            </>
          )}

          <TextField
            label="Notes"
            placeholder="Optional notes about this payment"
            value={form.notes}
            onChange={handleChange('notes')}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={form.sendReceipt}
                onChange={(e) => setForm(prev => ({ ...prev, sendReceipt: e.target.checked }))}
                sx={{ color: '#003063', '&.Mui-checked': { color: '#003063' } }}
              />
            }
            label={<Typography sx={{ fontSize: '0.575rem' }}>Send receipt to customer</Typography>}
          />
        </Box>

        {/* Secure Card Form */}
        {isCC && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LockIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#003063' }}>
                Secure Card Information
              </Typography>
              <CardIcon sx={{ color: '#666', fontSize: 20, ml: 'auto' }} />
            </Box>

            <Alert severity="info" sx={{ mb: 2, py: 0.5 }} icon={<LockIcon fontSize="small" />}>
              Card data is tokenized securely via Authorize.net. No raw card numbers are stored.
            </Alert>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Card Number"
                placeholder="4111 1111 1111 1111"
                value={cardData.cardNumber}
                onChange={handleCardChange('cardNumber')}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 19 }}
                sx={fieldSx}
                {...fieldError('cardNumber')}
              />

              <TextField
                label="Name on Card"
                placeholder="Full name as shown on card"
                value={cardData.nameOnCard}
                onChange={handleCardChange('nameOnCard')}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
                {...fieldError('nameOnCard')}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Expiration (MM/YY)"
                  placeholder="MM/YY"
                  value={expDisplay}
                  onChange={handleExpChange}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 5 }}
                  sx={fieldSx}
                  {...(cardErrors.expiration ? { error: true, helperText: cardErrors.expiration } : {})}
                />
                <TextField
                  label="CVV"
                  placeholder="123"
                  value={cardData.cardCode}
                  onChange={handleCardChange('cardCode')}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 4 }}
                  sx={fieldSx}
                  {...fieldError('cardCode')}
                />
              </Box>

              <Divider sx={{ my: 0.5 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#003063' }}>
                Billing Address
              </Typography>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="First Name"
                  placeholder="First name"
                  value={billing.firstName}
                  onChange={handleBillingChange('firstName')}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                  {...fieldError('firstName')}
                />
                <TextField
                  label="Last Name"
                  placeholder="Last name"
                  value={billing.lastName}
                  onChange={handleBillingChange('lastName')}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                  {...fieldError('lastName')}
                />
              </Box>

              <TextField
                label="Address"
                placeholder="Street address"
                value={billing.address}
                onChange={handleBillingChange('address')}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
                {...fieldError('address')}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="City"
                  placeholder="City"
                  value={billing.city}
                  onChange={handleBillingChange('city')}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                  {...fieldError('city')}
                />
                <TextField
                  select
                  label="State"
                  value={billing.state}
                  onChange={handleBillingChange('state')}
                  size="small"
                  sx={{ minWidth: 100, ...fieldSx }}
                  InputLabelProps={{ shrink: true }}
                >
                  {US_STATES.map(s => (
                    <MenuItem key={s} value={s} sx={menuItemSx}>{s}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="ZIP"
                  placeholder="12345"
                  value={billing.zip}
                  onChange={handleBillingChange('zip')}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 10 }}
                  sx={fieldSx}
                  {...fieldError('zip')}
                />
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={() => onClose(false)}
          variant="outlined"
          disabled={isSubmitting}
          sx={{ fontSize: '0.875rem' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || acceptLoading}
          sx={{ bgcolor: '#003063', '&:hover': { bgcolor: '#002145' }, fontSize: '0.875rem' }}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : (isCC ? <LockIcon /> : null)}
        >
          {isSubmitting ? 'Processing...' : (isCC ? 'Process Card Payment' : 'Apply Payment')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplyPaymentModal;